import React, { createContext, useState, useContext, useEffect } from 'react';
import apiClient from '../api/client';

/**
 * Context API para gerenciamento de autenticação
 * Conforme doc 06-guia-de-estilo-frontend.md
 */

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [precisaAlterarSenha, setPrecisaAlterarSenha] = useState(false);
  const [loading, setLoading] = useState(true);

    // Restaurar sessão do localStorage ao carregar
  useEffect(() => {
    const storedToken = localStorage.getItem('nerdzpoker_token');
    const storedUser = localStorage.getItem('nerdzpoker_user');
    const storedPrecisaAlterar = localStorage.getItem('nerdzpoker_precisa_alterar_senha');

    if (storedToken && storedUser) {
      try {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        if (storedPrecisaAlterar) {
          setPrecisaAlterarSenha(JSON.parse(storedPrecisaAlterar));
        }
      } catch (error) {
        console.error('Erro ao restaurar sessão:', error);
        localStorage.removeItem('nerdzpoker_token');
        localStorage.removeItem('nerdzpoker_user');
        localStorage.removeItem('nerdzpoker_precisa_alterar_senha');
      }
    }
    setLoading(false);
  }, []);

  const login = (newToken, newUser, precisaAlterar = false) => {
    if (!newToken || !newUser) {
      console.error('Login: token ou user inválido', { newToken, newUser });
      return;
    }
    
    console.log('AuthContext: Atualizando estado de autenticação', { user: newUser.email, precisaAlterarSenha: precisaAlterar });
    setToken(newToken);
    setUser(newUser);
    setPrecisaAlterarSenha(precisaAlterar);
    localStorage.setItem('nerdzpoker_token', newToken);
    localStorage.setItem('nerdzpoker_user', JSON.stringify(newUser));
    localStorage.setItem('nerdzpoker_precisa_alterar_senha', JSON.stringify(precisaAlterar));
    console.log('AuthContext: Estado atualizado com sucesso');
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setPrecisaAlterarSenha(false);
    localStorage.removeItem('nerdzpoker_token');
    localStorage.removeItem('nerdzpoker_user');
    localStorage.removeItem('nerdzpoker_precisa_alterar_senha');
  };

  const clearPrecisaAlterarSenha = () => {
    setPrecisaAlterarSenha(false);
    localStorage.removeItem('nerdzpoker_precisa_alterar_senha');
  };

  // Determinar role do usuário (admin tem precedência sobre role)
  const getUserRole = () => {
    if (!user) return null;
    if (user.is_admin || user.role === 'admin') return 'admin';
    return user.role || 'jogador';
  };

  const userRole = getUserRole();

  const value = {
    user,
    token,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || userRole === 'admin',
    isGerenciador: userRole === 'gerenciador',
    isJogador: userRole === 'jogador',
    role: userRole,
    precisaAlterarSenha,
    clearPrecisaAlterarSenha,
    // Permissões de acesso
    canAccessJogadores: userRole === 'admin', // Apenas admin pode gerenciar jogadores
    canAccessRodadas: userRole === 'admin' || userRole === 'gerenciador',
    canAccessFinanceiro: true, // Todos podem ver financeiro
    canAccessRanking: true, // Todos podem ver ranking
    canAccessNocautes: true, // Todos podem ver nocautes
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
